import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { components } from '@/api/schema';
import { formatUZS } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Search, FileText, Clock, DollarSign, AlertTriangle, Filter, Store } from 'lucide-react';
import ConsistencyBadge from '@/components/murabaha/ConsistencyBadge';
import { checkMurabahaConsistency } from '@/lib/murabaha-consistency';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { InvoiceBadge } from '@/components/invoices/InvoiceBadge';
import { InvoiceProblemBadge } from '@/components/payouts/PayoutBadge';
import { BankContractBadge } from '@/components/murabaha/BankContractBadge';
import { intlLocale } from './adminLocale';

type ContractRow = components['schemas']['ContractResource'];

const statusColors: Record<string, string> = {
  Draft: 'bg-muted text-muted-foreground',
  PendingCustomerConfirmation: 'bg-warning/10 text-warning',
  Approved: 'bg-info/10 text-info',
  Rejected: 'bg-destructive/10 text-destructive',
  Completed: 'bg-success/10 text-success',
  Cancelled: 'bg-muted text-muted-foreground',
};

const statusLabels = (t: TFunction): Record<string, string> => ({
  Draft: t('adm_contracts.st_draft'),
  PendingCustomerConfirmation: t('adm_contracts.st_pending'),
  Approved: t('adm_contracts.st_approved'),
  Rejected: t('adm_contracts.st_rejected'),
  Completed: t('adm_contracts.st_completed'),
  Cancelled: t('adm_contracts.st_cancelled'),
});

const paymentStatusColors: Record<string, string> = {
  none: 'bg-muted text-muted-foreground',
  in_progress: 'bg-warning/10 text-warning',
  cancelled: 'bg-destructive/10 text-destructive',
  paid: 'bg-success/10 text-success',
};

const paymentStatusLabels = (t: TFunction): Record<string, string> => ({
  none: '—',
  in_progress: t('adm_contracts.pay_inProgress'),
  cancelled: t('adm_contracts.pay_cancelled'),
  paid: t('adm_contracts.pay_paid'),
});

const statusFilters = (t: TFunction) => [
  { key: 'Draft', label: t('adm_contracts.st_draft') },
  { key: 'PendingCustomerConfirmation', label: t('adm_contracts.st_pending') },
  { key: 'Approved', label: t('adm_contracts.st_approved') },
  { key: 'Completed', label: t('adm_contracts.st_completed') },
  { key: 'Rejected', label: t('adm_contracts.st_rejected') },
  { key: 'Cancelled', label: t('adm_contracts.st_cancelled') },
];

const paymentFilters = (t: TFunction) => [
  { key: 'none', label: t('adm_contracts.pay_none') },
  { key: 'in_progress', label: t('adm_contracts.pay_inProgress') },
  { key: 'paid', label: t('adm_contracts.pay_paid') },
  { key: 'cancelled', label: t('adm_contracts.pay_cancelled') },
];

export default function AdminContracts() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [merchantFilter, setMerchantFilter] = useState('all');
  const [combinedFilter, setCombinedFilter] = useState('all');

  const { data: contractsData, isLoading: loadingContracts } = useQuery({ queryKey: ['/admin/contracts'] });
  const contracts = (contractsData?.data ?? []) as ContractRow[];

  const { data: merchantsData } = useQuery({ queryKey: ['/admin/merchants'] });
  const merchants = merchantsData?.data ?? [];

  const { data: customersData } = useQuery({ queryKey: ['/admin/customers'] });
  const customers = customersData?.data ?? [];

  const loading = loadingContracts;

  const getMerchant = (merchantId?: number) => merchants.find(m => m.id === merchantId);
  const getCustomer = (customerId?: number) => customers.find(c => c.id === customerId);

  // Lovable's unified ID: the case's own contract number first, then the contract row's.
  const contractIdOf = (c: ContractRow) => c.murabaha_case?.contract_number || c.contract_number || null;
  const issuesOf = (c: ContractRow, mc: ContractRow['murabaha_case']) =>
    mc ? checkMurabahaConsistency({ ...mc, contract_id: mc.contract_number }, { status: c.status, contract_number: c.contract_number }).issues : [];

  const statusFilter = combinedFilter.startsWith('s:') ? combinedFilter.slice(2) : 'all';
  const paymentFilter = combinedFilter.startsWith('p:') ? combinedFilter.slice(2) : 'all';
  const onlyMismatch = combinedFilter === 'mismatch';

  const filtered = contracts.filter(c => {
    const mc = c.murabaha_case;
    const merchant = mc ? getMerchant(mc.merchant_id) : null;
    const customer = mc ? getCustomer(mc.customer_id) : null;
    const q = search.toLowerCase();
    const matchSearch = !search ||
      (contractIdOf(c) || '').toLowerCase().includes(q) ||
      merchant?.name?.toLowerCase().includes(q) ||
      customer?.full_name?.toLowerCase().includes(q);
    const matchMerchant = merchantFilter === 'all' || mc?.merchant_id === Number(merchantFilter);
    const matchStatus = statusFilter === 'all' || mc?.status === statusFilter;
    const matchPayment = paymentFilter === 'all' || (mc?.payment_status ?? 'none') === paymentFilter;
    const matchMismatch = !onlyMismatch || issuesOf(c, mc).length > 0;
    return matchSearch && matchMerchant && matchStatus && matchPayment && matchMismatch;
  });

  const mismatchCount = contracts.filter(c => issuesOf(c, c.murabaha_case).length > 0).length;
  const pendingContracts = contracts.filter(c => {
    const mc = c.murabaha_case;
    return mc?.status === 'Draft' || mc?.status === 'PendingCustomerConfirmation';
  });
  const pendingSum = pendingContracts.reduce((s, c) => s + Number(c.total_amount), 0);

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('adm_contracts.title')}</h1>
        <p className="text-muted-foreground text-sm">{t('adm_contracts.subtitle')}</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} className="text-warning" />
            <span className="text-xs text-muted-foreground">{t('adm_contracts.pendingContracts')}</span>
          </div>
          <p className="text-lg font-semibold text-foreground">{loading ? '...' : t('adm_contracts.countPcs', { count: pendingContracts.length })}</p>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={16} className="text-warning" />
            <span className="text-xs text-muted-foreground">{t('adm_contracts.pendingSum')}</span>
          </div>
          <p className="text-lg font-semibold text-foreground">{loading ? '...' : formatUZS(pendingSum)}</p>
        </div>
        <button
          type="button"
          onClick={() => setCombinedFilter(onlyMismatch ? 'all' : 'mismatch')}
          className={`text-left bg-card rounded-xl border p-4 transition-colors hover:bg-muted/30 ${
            mismatchCount > 0 ? 'border-destructive/40' : 'border-border'
          } ${onlyMismatch ? 'ring-2 ring-destructive/40' : ''}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className={mismatchCount > 0 ? 'text-destructive' : 'text-muted-foreground'} />
            <span className="text-xs text-muted-foreground">{t('adm_contracts.mismatches')}</span>
          </div>
          <p className={`text-lg font-semibold ${mismatchCount > 0 ? 'text-destructive' : 'text-foreground'}`}>
            {loading ? '...' : t('adm_contracts.countPcs', { count: mismatchCount })}
          </p>
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder={t('adm_contracts.searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={merchantFilter} onValueChange={setMerchantFilter}>
          <SelectTrigger className="w-[200px]">
            <Store size={14} className="text-muted-foreground mr-1" />
            <SelectValue placeholder={t('adm_contracts.store')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('adm_contracts.allStores')}</SelectItem>
            {merchants.map(m => (
              <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={combinedFilter} onValueChange={setCombinedFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter size={14} className="text-muted-foreground mr-1" />
            <SelectValue placeholder={t('adm_common.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('adm_common.all')}</SelectItem>
            <SelectItem value="mismatch">{t('adm_contracts.onlyMismatches')}</SelectItem>
            <SelectSeparator />
            <SelectGroup>
              <SelectLabel>{t('adm_contracts.status')}</SelectLabel>
              {statusFilters(t).map(f => (
                <SelectItem key={f.key} value={`s:${f.key}`}>{f.label}</SelectItem>
              ))}
            </SelectGroup>
            <SelectSeparator />
            <SelectGroup>
              <SelectLabel>{t('adm_contracts.payment')}</SelectLabel>
              {paymentFilters(t).map(f => (
                <SelectItem key={f.key} value={`p:${f.key}`}>{f.label}</SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-muted-foreground py-8">{t('adm_common.loading')}</div>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('adm_contracts.contractId')}</TableHead>
                <TableHead>{t('adm_contracts.store')}</TableHead>
                <TableHead>{t('adm_contracts.customer')}</TableHead>
                <TableHead>{t('adm_contracts.contractAmount')}</TableHead>
                <TableHead>{t('adm_contracts.payoutToStore')}</TableHead>
                <TableHead>{t('adm_contracts.term')}</TableHead>
                <TableHead>{t('adm_contracts.status')}</TableHead>
                <TableHead>{t('invoice.column')}</TableHead>
                <TableHead>{t('adm_contracts.payment')}</TableHead>
                <TableHead>{t('adm_contracts.bank')}</TableHead>
                <TableHead>{t('adm_common.date')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                    <FileText className="mx-auto mb-2 h-8 w-8 opacity-30" />
                    {t('adm_contracts.notFound')}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map(c => {
                  const mc = c.murabaha_case;
                  const merchant = mc ? getMerchant(mc.merchant_id) : null;
                  const customer = mc ? getCustomer(mc.customer_id) : null;
                  const issues = issuesOf(c, mc);
                  const contractTotal = Number(c.total_amount);
                  // Do'konga to'lanadi = order total (goods price); markup = the rest (as Lovable).
                  const payout = mc?.orders?.[0] ? Number(mc.orders[0].total_amount) : contractTotal;
                  const markup = Math.max(0, contractTotal - payout);
                  const unifiedId = contractIdOf(c);
                  const caseStatus = mc?.status;
                  const paymentStatus = mc?.payment_status ?? 'none';
                  return (
                    <TableRow key={c.id} className={`cursor-pointer ${issues.length > 0 ? 'bg-destructive/5' : ''}`} onClick={() => navigate(`/admin/contracts/${c.id}`)}>
                      <TableCell className="font-mono font-medium text-xs">
                        {unifiedId
                          ? unifiedId
                          : <span className="text-muted-foreground font-sans">
                              {caseStatus === 'Approved' || caseStatus === 'Completed' ? t('adm_contracts.idNotAssigned') : t('adm_contracts.awaiting')}
                            </span>}
                      </TableCell>
                      <TableCell className="text-sm">{merchant?.name || '—'}</TableCell>
                      <TableCell className="text-sm">{customer?.full_name || '—'}</TableCell>
                      <TableCell className="text-sm">
                        {formatUZS(contractTotal)}
                        {markup > 0 && <span className="block text-[10px] text-muted-foreground">{t('adm_contracts.markup')} {formatUZS(markup)}</span>}
                      </TableCell>
                      <TableCell className="text-sm font-medium text-success">
                        {formatUZS(payout)}
                        <span className="block text-[10px] text-muted-foreground font-normal">{t('adm_contracts.goodsPrice')}</span>
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {t('adm_contracts.monthsCount', { count: c.term_months })}
                        <span className="block text-[10px] text-muted-foreground">{formatUZS(Number(c.monthly_payment))}/{t('adm_contracts.perMonth')}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <Badge className={`text-xs border-0 ${statusColors[caseStatus || ''] || 'bg-muted text-muted-foreground'}`}>
                            {statusLabels(t)[caseStatus || ''] || caseStatus || '—'}
                          </Badge>
                          <ConsistencyBadge issues={issues} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <InvoiceBadge status={mc?.invoice_status} />
                          <InvoiceProblemBadge show={mc?.invoice_problem} />
                        </div>
                      </TableCell>
                      <TableCell>
                        {paymentStatus !== 'none' ? (
                          <Badge className={`text-xs border-0 ${paymentStatusColors[paymentStatus] || ''}`}>
                            {paymentStatusLabels(t)[paymentStatus] || paymentStatus}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {c.bank_status ? <BankContractBadge status={c.bank_status} labels="adm_contracts.bank_st" /> : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString(intlLocale()) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
